/**
 * cropClass (HOF-467) — the Ackerbau-vs-Beeren discriminator behind the drawer's
 * Schichten / KTBL regrouping toggle.
 *
 * No such field reaches the frontend: the fact carries ``crop: string`` and the worker
 * holds its canonical-crop folds, but no Betriebszweig or cultivation-system attribute
 * is on the fact (adding one is a materialiser change the issue forbids, and reading
 * ``config/`` from here drags the repo-root path scanner in for a display toggle). So
 * this is a STATIC map beside ``BASIS_META`` — the same "not per-tenant data, so it lives
 * in the FE, not the fact" precedent — keyed on the canonical crop and **fail-closed**:
 * a crop the map does not classify gets NO toggle rather than a wrong one. Reversal
 * for a missed arable crop is one entry here.
 */

/** Umlaut-folded, lower-cased crop keys — what ``foldCrop`` produces. */
const ARABLE_CROPS: ReadonlySet<string> = new Set([
  // Getreide
  'weizen',
  'winterweizen',
  'sommerweizen',
  'dinkel',
  'roggen',
  'winterroggen',
  'gerste',
  'wintergerste',
  'sommergerste',
  'hafer',
  'triticale',
  'hirse',
  // Ölsaaten / Leguminosen
  'raps',
  'winterraps',
  'sommerraps',
  'sonnenblumen',
  'ackerbohnen',
  'erbsen',
  'koernererbsen',
  'sojabohnen',
  'lupinen',
  // Hackfrüchte / Mais
  'kartoffeln',
  'zuckerrueben',
  'mais',
  'koernermais',
  'silomais',
]);

/** Lower-case, whitespace-collapsed, umlauts folded (ü→ue, ß→ss) — so ``Zuckerrüben`` and ``Zuckerrueben`` meet. */
export const foldCrop = (crop: string): string =>
  crop
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');

/** True only for a crop the static map names as arable — anything else is not classified. */
export function isArableCrop(crop: string | null | undefined): boolean {
  if (!crop) return false;
  return ARABLE_CROPS.has(foldCrop(crop));
}
